import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import selectedOverlay from 'public/images/FullScreenFeatureRotator_Selected_Shell.png';
import unselectedOverlay from 'public/images/FullScreenFeatureRotator_Unselected_Shell.png';
import {
  PREVIEW_SPECS,
  CANVAS_SPECS,
  LOGO_DIMENSIONS,
  BACKGROUND_UPSCALED_DIMENSIONS,
} from 'src/configs/previewConfig';
import { ErrorConstants, PreviewConstants } from 'src/constants';
import { logger } from 'src/logger';
import { PreviewState, PreviewAssets } from 'src/types';
import { loadImage, upscaleImage } from 'src/utils/ImageUtils';

const OVERLAY_IMAGES: Record<
  Exclude<PreviewState, typeof PreviewConstants.PREVIEW_LABELS.VIDEO>,
  string
> = {
  [PreviewConstants.PREVIEW_LABELS.SELECTED]: selectedOverlay,
  [PreviewConstants.PREVIEW_LABELS.UNSELECTED]: unselectedOverlay,
} as const;

const createCanvas = (width: number, height: number) => {
  const canvas = document.createElement(PreviewConstants.CANVAS.ELEMENT);
  const ctx = canvas.getContext(PreviewConstants.CANVAS.CONTEXT);
  if (!ctx) {
    throw new Error(ErrorConstants.MESSAGES.CANVAS_CONTEXT_NOT_FOUND);
  }
  canvas.width = width;
  canvas.height = height;
  return { canvas, ctx };
};

/**
 * Draws background image on canvas with specified configuration
 */
const drawBackground = (
  ctx: CanvasRenderingContext2D,
  bgImage: HTMLImageElement,
  stateConfig: (typeof PREVIEW_SPECS.states)[keyof typeof PREVIEW_SPECS.states],
  dimensions: typeof PREVIEW_SPECS.dimensions,
) => {
  ctx.fillStyle = CANVAS_SPECS.backgroundColor;
  ctx.fillRect(
    CANVAS_SPECS.position.x,
    CANVAS_SPECS.position.y,
    dimensions.width,
    dimensions.height,
  );
  ctx.drawImage(
    bgImage,
    stateConfig.background.x,
    stateConfig.background.y,
    bgImage.width,
    bgImage.height,
  );
};

const drawLogo = (
  ctx: CanvasRenderingContext2D,
  logoImage: HTMLImageElement,
  stateConfig: (typeof PREVIEW_SPECS.states)[keyof typeof PREVIEW_SPECS.states],
) => {
  ctx.drawImage(
    logoImage,
    stateConfig.logo.x,
    stateConfig.logo.y,
    LOGO_DIMENSIONS.width,
    LOGO_DIMENSIONS.height,
  );
};

export const generatePreview = async (previewAssets: PreviewAssets, previewState: PreviewState) => {
  try {
    if (
      previewState !== PreviewConstants.PREVIEW_LABELS.SELECTED &&
      previewState !== PreviewConstants.PREVIEW_LABELS.UNSELECTED
    ) {
      throw new Error(
        `Invalid preview state: ${previewState}. Expected either ` +
          `${PreviewConstants.PREVIEW_LABELS.SELECTED} or ` +
          `${PreviewConstants.PREVIEW_LABELS.UNSELECTED}`,
      );
    }

    if (!previewAssets[AssetType.BACKGROUND_IMAGE] || !previewAssets[AssetType.LOGO]) {
      throw new Error('Missing required images. Both background image and logo are required.');
    }
    let [bgImage, logoImage, overlay] = await Promise.all([
      loadImage(previewAssets[AssetType.BACKGROUND_IMAGE]!),
      loadImage(previewAssets[AssetType.LOGO]!),
      loadImage(OVERLAY_IMAGES[previewState]),
    ]);
    if (previewState === PreviewConstants.PREVIEW_LABELS.SELECTED) {
      let upscaledImageUrl = await upscaleImage(
        bgImage,
        BACKGROUND_UPSCALED_DIMENSIONS.width,
        BACKGROUND_UPSCALED_DIMENSIONS.height,
      );
      bgImage = await loadImage(upscaledImageUrl);
    }
    const { width, height } = PREVIEW_SPECS.dimensions;
    const stateConfig = PREVIEW_SPECS.states[previewState];

    const canvas = createCanvas(width, height);
    const tempCanvas = createCanvas(width, height);

    drawBackground(tempCanvas.ctx, bgImage, stateConfig, PREVIEW_SPECS.dimensions);
    tempCanvas.ctx.drawImage(overlay, stateConfig.overlay.x, stateConfig.overlay.y, width, height);
    drawLogo(tempCanvas.ctx, logoImage, stateConfig);
    canvas.ctx.drawImage(tempCanvas.canvas, 0, stateConfig.containerOffset, width, height);

    const generatedUrl = canvas.canvas.toDataURL(
      CANVAS_SPECS.imageFormat,
      CANVAS_SPECS.imageQuality,
    );
    return generatedUrl;
  } catch (error) {
    logger.error('Preview generation failed', {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};
